import { create } from 'zustand';
import type {
  ExpenditureRecordResponse,
  ExpenditureSummaryForRevenueAxisAppropriationResponse,
  RevenueRecordForRevenueAxisAppropriationResponse,
  RevenueRecordResponse,
  SettlementStatisticsExpenditurePurposeSchema,
} from '@/entities/settlementstatisticsconverter/api/generated/model';
import type { MasterType } from '@/entities/settlementstatisticsconverter/lib/master-type';

/**
 * SidePanelのトランジション時間（ミリ秒）
 * CSSのトランジション時間（0.4s cubic-bezier）と一致させる必要がある
 */
export const SIDE_PANEL_TRANSITION_DURATION = 400; // ms

/**
 * SidePanelのトランジションCSS
 * PageLayoutFrameやSidePanelコンポーネントで使用
 */
export const SIDE_PANEL_TRANSITION_CSS = '0.4s cubic-bezier(0.4, 0, 0.2, 1)';

/**
 * AIコンテキストの型定義
 * サイドパネルでのAI機能のコンテキストを管理
 */
export type AIContext =
  | { route: 'projectCreate'; mode: 'createFromPdf' }
  | { route: 'projectEdit'; mode: 'createFromPdf' }
  | { route: 'projectDetail'; mode: 'agent_chat'; threadId?: string };

/**
 * サイドパネルモードの型定義（ネスト形式）
 *
 * @example
 * ```tsx
 * // AI機能モード（コンテキスト付き）
 * const mode: SidePanelMode = {
 *   type: 'ai-chat',
 *   context: { route: 'projectCreate', mode: 'createFromPdf' }
 * };
 *
 * // 評価比較モード
 * const mode: SidePanelMode = { type: 'assessment-comparison' };
 * ```
 */
export type SidePanelMode =
  | { type: 'ai-chat'; context?: AIContext }
  | { type: 'assessment-comparison' }
  | {
      type: 'settlement-statistics-converter.revenue-edit';
      revenue: RevenueRecordResponse;
      convertResultId: string;
    }
  | {
      type: 'settlement-statistics-converter.expenditure-edit';
      expenditure: ExpenditureRecordResponse;
      convertResultId: string;
    }
  | {
      type: 'settlement-statistics-converter.master';
      convertResultId: string;
      masterType: MasterType;
      /** 編集対象（undefinedなら新規追加モード） */
      editTarget?: Pick<
        SettlementStatisticsExpenditurePurposeSchema,
        | 'id'
        | 'level1_code'
        | 'level1_name'
        | 'level2_code'
        | 'level2_name'
        | 'level3_code'
        | 'level3_name'
        | 'level4_code'
        | 'level4_name'
      >;
    }
  | ({
      type: 'settlement-statistics-converter.revenue-appropriation';
      convertResultId: string;
    } & (
      | {
          mode: 'create';
          revenueRecordId: string;
          revenueRecord: RevenueRecordForRevenueAxisAppropriationResponse;
        }
      | {
          mode: 'edit';
          revenueAppropriationId: string;
          expenditureSummary: ExpenditureSummaryForRevenueAxisAppropriationResponse;
        }
    ))
  | ({
      type: 'master-management.accounting-types';
      fiscalYear: number;
    } & (
      | { mode: 'create' }
      | {
          mode: 'edit';
          accountingType: import('@/entities/masters/api/generated/model').AccountingTypeResponse;
        }
    ))
  | ({
      type: 'master-management.expenditure-items';
      fiscalYear: number;
    } & (
      | { mode: 'create' }
      | {
          mode: 'edit';
          item: { id: string; code: string; name: string; level: 'kan' | 'ko' | 'moku' };
        }
    ))
  | ({
      type: 'master-management.organizations';
      fiscalYear: number;
    } & (
      | { mode: 'create' }
      | {
          mode: 'edit';
          organization: import('@/entities/tenants-divisions/api/generated/model').TenantDivisionListItemSchema;
        }
    ))
  | undefined;

/** 未保存変更の確認ダイアログでユーザーの応答を待っているアクションの種別 */
type PendingAction = 'close' | 'modeChange' | 'callback' | null;

type SidePanelState = {
  // ─────────────────────────────────────────────
  // パネル状態
  // ─────────────────────────────────────────────
  /** パネル開閉状態 */
  isOpen: boolean;
  /** パネルモード */
  mode: SidePanelMode;
  /** 未保存の変更があるかどうか（trueの場合、閉じる/モード変更時に確認ダイアログを表示） */
  hasUnsavedChanges: boolean;
  /** 未保存変更がある場合にアクションをブロックするかを判定する関数（falseを返すとブロック） */
  unsavedChangesGuard: (() => boolean) | null;
  /** 確認ダイアログでユーザーの応答を待っているアクション */
  pendingAction: PendingAction;
  /** 確認待ちのモード（pendingAction === 'modeChange' 時のみ使用） */
  pendingMode: SidePanelMode;
  /** 確認待ちのコールバック（pendingAction === 'callback' 時のみ使用） */
  pendingCallback: (() => void) | null;

  // ─────────────────────────────────────────────
  // パネル操作
  // ─────────────────────────────────────────────
  /** パネルの開閉状態を設定（ガードなし、内部用） */
  setOpen: (params: { isOpen: boolean; mode: SidePanelMode }) => void;

  // ─────────────────────────────────────────────
  // 未保存変更の検知・離脱確認ガード
  // ─────────────────────────────────────────────
  /** 未保存変更の有無を設定する */
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  /** 未保存変更ガード関数を設定する（SidePanelLeaveAlertが登録し、アンマウント時にnullで解除） */
  setUnsavedChangesGuard: (guard: (() => boolean) | null) => void;

  // ─────────────────────────────────────────────
  // 未保存変更がある場合に確認を挟むアクション
  // ─────────────────────────────────────────────
  /** パネルを閉じる（未保存変更がある場合は確認ダイアログを表示） */
  requestClose: () => void;
  /** モードを変更する（未保存変更がある場合は確認ダイアログを表示） */
  requestModeChange: (mode: SidePanelMode) => void;
  /** 任意のアクションを実行する（未保存変更がある場合は確認ダイアログを表示） */
  requestAction: (callback: () => void) => void;
  /** 確認ダイアログで「OK」を押した際に、保留中のアクションを実行する */
  confirmPendingAction: () => void;
  /** 確認ダイアログで「キャンセル」を押した際に、保留中のアクションを破棄する */
  cancelPendingAction: () => void;
};

/**
 * 右SidePanelの状態を管理するZustandストア
 *
 * - パネルの開閉・モード管理
 * - ダーティ状態とガード機能
 *
 * ※ 左パネル（サイドナビゲーション）は useSideNavigationStore で管理
 */
export const useRouteSidePanelStore = create<SidePanelState>((set, get) => ({
  // ─────────────────────────────────────────────
  // 初期状態
  // ─────────────────────────────────────────────
  isOpen: false,
  mode: undefined,
  hasUnsavedChanges: false,
  unsavedChangesGuard: null,
  pendingAction: null,
  pendingMode: undefined,
  pendingCallback: null,

  // ─────────────────────────────────────────────
  // パネル操作
  // ─────────────────────────────────────────────
  setOpen: ({ isOpen, mode }) => set({ isOpen, mode }),

  // ─────────────────────────────────────────────
  // 未保存変更の検知・離脱確認ガード
  // ─────────────────────────────────────────────
  setHasUnsavedChanges: (hasUnsavedChanges) => set({ hasUnsavedChanges }),

  setUnsavedChangesGuard: (guard) => set({ unsavedChangesGuard: guard }),

  // ─────────────────────────────────────────────
  // 未保存変更がある場合に確認を挟むアクション
  // ─────────────────────────────────────────────
  requestClose: () => {
    const { unsavedChangesGuard } = get();

    if (!unsavedChangesGuard || unsavedChangesGuard()) {
      set({
        isOpen: false,
        mode: undefined,
        pendingAction: null,
        pendingMode: undefined,
        pendingCallback: null,
      });
    } else {
      set({
        pendingAction: 'close',
        pendingMode: undefined,
        pendingCallback: null,
      });
    }
  },

  requestModeChange: (mode) => {
    const { unsavedChangesGuard, mode: currentMode } = get();

    if (currentMode === mode) return;

    if (!unsavedChangesGuard || unsavedChangesGuard()) {
      set({
        mode,
        pendingAction: null,
        pendingMode: undefined,
        pendingCallback: null,
      });
    } else {
      set({
        pendingAction: 'modeChange',
        pendingMode: mode,
        pendingCallback: null,
      });
    }
  },

  requestAction: (callback) => {
    const { unsavedChangesGuard } = get();

    if (!unsavedChangesGuard || unsavedChangesGuard()) {
      callback();
    } else {
      set({
        pendingAction: 'callback',
        pendingMode: undefined,
        pendingCallback: callback,
      });
    }
  },

  confirmPendingAction: () => {
    const { pendingAction, pendingMode, pendingCallback } = get();

    if (pendingAction === 'close') {
      set({
        isOpen: false,
        mode: undefined,
        pendingAction: null,
        pendingMode: undefined,
        pendingCallback: null,
      });
    } else if (pendingAction === 'modeChange') {
      set({
        mode: pendingMode,
        pendingAction: null,
        pendingMode: undefined,
        pendingCallback: null,
      });
    } else if (pendingAction === 'callback' && pendingCallback) {
      pendingCallback();
      set({
        pendingAction: null,
        pendingMode: undefined,
        pendingCallback: null,
      });
    }
  },

  cancelPendingAction: () =>
    set({
      pendingAction: null,
      pendingMode: undefined,
      pendingCallback: null,
    }),
}));
